import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getCourseDetail, getProgress, updateLessonProgress } from "@/services/api";
import Layout from "@/components/Layout";
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, Smartphone, BookOpen } from "lucide-react";
import axios from "axios";
import { AppFeatureShowcase, FeatureCallout } from "@/components/AppFeatureShowcase";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

export function PhoneMockup({ image }) {
  return (
    <div className="flex justify-center">
      <div
        className="relative"
        style={{
          width: 320,
          background: "#242220",
          borderRadius: "48px",
          border: "5px solid #242220",
          boxShadow: "10px 10px 0 #242220",
          padding: "16px 12px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            width: 70,
            height: 14,
            background: "#333",
            borderRadius: 99,
            zIndex: 2,
          }}
        />
        <div
          style={{
            background: "#FDF5EB",
            borderRadius: "36px",
            overflow: "hidden",
            minHeight: 520,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {image ? (
            <img
              src={image}
              alt="App screenshot"
              style={{ width: "100%", height: 520, objectFit: "contain", background: "#FDF5EB" }}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 p-4">
              <Smartphone size={64} strokeWidth={1.5} color="#242220" style={{ opacity: 0.3 }} />
              <p className="text-xs text-center font-bold" style={{ color: "#242220", opacity: 0.4 }}>
                NaturTrak App
              </p>
            </div>
          )}
        </div>
        <div
          style={{
            width: 80,
            height: 16,
            background: "#555",
            borderRadius: 99,
            margin: "12px auto 0",
          }}
        />
      </div>
    </div>
  );
}

function QuizSection({ questions, lessonId, courseIdentifier, userId, token, onPass, alreadyPassed, onRetry }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [passed, setPassed] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const handleSelect = (qId, optIdx) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [qId]: optIdx }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      alert("Kérjük, válaszolj minden kérdésre!");
      return;
    }
    setSubmitting(true);
    setSubmitError(false);
    try {
      const res = await axios.post(
        `${BASE}/courses/${encodeURIComponent(courseIdentifier)}/lesson/${lessonId}/quiz/submit`,
        { user_id: userId, answers },
        { headers: authHdr(token) }
      );
      const { score: s, passed: p, feedback: fb } = res.data;
      setScore(s);
      setPassed(p);
      setFeedback(fb);
      setSubmitted(true);
      await updateLessonProgress(token, {
        user_id: userId,
        course_identifier: courseIdentifier,
        lesson_id: lessonId,
        quiz_score: s,
        quiz_passed: p,
      });
      if (p) setTimeout(() => onPass(), 1500);
    } catch (e) {
      console.error("Quiz submit error", e);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(null);
    setPassed(false);
    setFeedback(null);
    setSubmitError(false);
    onRetry?.();
  };

  if (alreadyPassed && !submitted) {
    return (
      <div
        className="mt-8 p-6 flex items-center gap-4"
        style={{
          background: "#A1E1B9",
          borderRadius: "24px",
          border: "1.5px solid #242220",
          boxShadow: "4px 4px 0 #242220",
        }}
        data-testid="quiz-already-passed"
      >
        <CheckCircle2 size={28} strokeWidth={2.5} color="#242220" />
        <div>
          <p className="font-bold text-base" style={{ color: "#242220" }}>Kvíz már teljesítve!</p>
          <p className="text-sm" style={{ color: "#242220", opacity: 0.7 }}>Ezt a leckét sikeresen befejezted.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-8 p-6"
      style={{
        background: "#fff",
        borderRadius: "24px",
        border: "1.5px solid #242220",
        boxShadow: "6px 6px 0 #242220",
      }}
      data-testid="quiz-section"
    >
      <h3 className="text-xl font-bold mb-2" style={{ color: "#242220" }}>Lecke Kvíz</h3>
      <p className="text-sm mb-6" style={{ color: "#242220", opacity: 0.6 }}>
        Az összes kérdésre válaszolj, majd küldd be a válaszaidat. (Min. 70% kell a teljesítéshez.)
      </p>

      {submitted && (
        <div
          className="flex items-center gap-3 p-4 mb-6"
          style={{
            background: passed ? "#A1E1B9" : "#FDECEA",
            border: `1.5px solid ${passed ? "#242220" : "#D32F2F"}`,
            borderRadius: "16px",
          }}
          data-testid="quiz-result"
        >
          {passed ? (
            <CheckCircle2 size={22} strokeWidth={2.5} color="#242220" />
          ) : (
            <XCircle size={22} strokeWidth={2.5} color="#D32F2F" />
          )}
          <div>
            <p className="font-bold text-base" style={{ color: passed ? "#242220" : "#D32F2F" }}>
              {passed ? "Helyes! Átmentél a kvízen." : "Sikertelen. Próbáld újra!"}
            </p>
            <p className="text-sm" style={{ color: "#242220", opacity: 0.7 }}>Eredmény: {score}%</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {questions.map((q, qi) => {
          const selected = answers[q.id];
          const fb = feedback?.[q.id];
          const correctIdx = fb?.correct_option_index;
          return (
            <div key={q.id} data-testid={`quiz-question-${qi}`}>
              {q.image ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left column - Question and answers */}
                  <div>
                    <p className="font-bold text-sm mb-3" style={{ color: "#242220" }}>
                      {qi + 1}. {q.question}
                    </p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => {
                        let bg = "#FDF5EB";
                        let borderColor = "#242220";
                        if (submitted && fb) {
                          if (oi === correctIdx) bg = "#A1E1B9";
                          else if (oi === fb.your_answer && !fb.is_correct) { bg = "#FDECEA"; borderColor = "#D32F2F"; }
                        } else if (selected === oi) {
                          bg = "#FBD556";
                        }
                        return (
                          <button
                            key={oi}
                            onClick={() => handleSelect(q.id, oi)}
                            className="w-full text-left px-4 py-3 text-sm font-bold"
                            style={{
                              background: bg,
                              borderRadius: "16px",
                              border: `1.5px solid ${borderColor}`,
                              boxShadow: selected === oi && !submitted ? "2px 2px 0 #242220" : "none",
                              color: "#242220",
                              cursor: submitted ? "default" : "pointer",
                            }}
                            data-testid={`quiz-option-${qi}-${oi}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Right column - Image */}
                  <div className="flex items-start">
                    <img
                      src={q.image}
                      alt=""
                      className="w-full object-contain"
                      style={{
                        maxHeight: 300,
                        borderRadius: "16px",
                        border: "1.5px solid #242220",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className="font-bold text-sm mb-3" style={{ color: "#242220" }}>
                    {qi + 1}. {q.question}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      let bg = "#FDF5EB";
                      let borderColor = "#242220";
                      if (submitted && fb) {
                        if (oi === correctIdx) bg = "#A1E1B9";
                        else if (oi === fb.your_answer && !fb.is_correct) { bg = "#FDECEA"; borderColor = "#D32F2F"; }
                      } else if (selected === oi) {
                        bg = "#FBD556";
                      }
                      return (
                        <button
                          key={oi}
                          onClick={() => handleSelect(q.id, oi)}
                          className="w-full text-left px-4 py-3 text-sm font-bold"
                          style={{
                            background: bg,
                            borderRadius: "16px",
                            border: `1.5px solid ${borderColor}`,
                            boxShadow: selected === oi && !submitted ? "2px 2px 0 #242220" : "none",
                            color: "#242220",
                            cursor: submitted ? "default" : "pointer",
                          }}
                          data-testid={`quiz-option-${qi}-${oi}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              {submitted && fb?.explanation && (
                <p
                  className="mt-2 text-xs px-3 py-2"
                  style={{
                    background: "#FDF5EB",
                    borderRadius: "10px",
                    border: "1px solid #A1E1B9",
                    color: "#242220",
                    opacity: 0.8,
                  }}
                >
                  {fb.explanation}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {submitError && (
        <div
          className="flex flex-col items-start gap-3 p-4 mb-6"
          style={{
            background: "#FDECEA",
            border: "1.5px solid #D32F2F",
            borderRadius: "16px",
          }}
          data-testid="quiz-submit-error"
        >
          <p className="text-sm font-bold" style={{ color: "#D32F2F" }}>
            A válaszok beküldése nem sikerült. A lecke adatai időközben frissülhettek – töltsd újra az oldalt, majd próbáld újra.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-bold transition-all active:translate-x-[2px] active:translate-y-[2px]"
            style={{
              background: "#FF9456",
              borderRadius: "20px",
              border: "1.5px solid #242220",
              boxShadow: "3px 3px 0 #242220",
              color: "#242220",
              cursor: "pointer",
            }}
            data-testid="quiz-reload-btn"
          >
            Oldal újratöltése
          </button>
        </div>
      )}

      <div className="flex gap-3 mt-6">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
            style={{
              background: "#FBD556",
              borderRadius: "30px",
              border: "1.5px solid #242220",
              boxShadow: "4px 4px 0 #242220",
              color: "#242220",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
            data-testid="quiz-submit-btn"
          >
            {submitting ? "Küldés..." : "Válasz beküldése"}
          </button>
        ) : !passed ? (
          <button
            onClick={handleRetry}
            className="flex-1 py-3 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
            style={{
              background: "#FF9456",
              borderRadius: "30px",
              border: "1.5px solid #242220",
              boxShadow: "4px 4px 0 #242220",
              color: "#242220",
              cursor: "pointer",
            }}
            data-testid="quiz-retry-btn"
          >
            Újra próbálom
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function LessonPage() {
  const { courseId, lessonId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizDone, setQuizDone] = useState(false);
  const [quizKey, setQuizKey] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cRes, pRes] = await Promise.all([
          getCourseDetail(token, decodeURIComponent(courseId)),
          getProgress(token, user.id),
        ]);
        const courseData = cRes.data;
        const prog = (pRes.data || []).find(
          (p) => p.course_identifier === decodeURIComponent(courseId)
        );
        setCourse(courseData);
        setProgress(prog || null);
        const lsn = (courseData.lessons || []).find((l) => l.id === lessonId);
        setLesson(lsn || null);
        console.log("Loaded lesson:", lsn);
        console.log("App features:", lsn?.app_features);
        const alreadyDone = (prog?.lessons_completed || []).includes(lessonId);
        setQuizDone(alreadyDone);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, lessonId, token, user]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64" style={{ color: "#242220" }}>
          <p className="font-bold opacity-40">Lecke betöltése...</p>
        </div>
      </Layout>
    );
  }

  if (!lesson) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="font-bold" style={{ color: "#242220" }}>Lecke nem található.</p>
          <button onClick={() => navigate(`/courses/${courseId}`)} className="mt-4 text-sm underline" style={{ color: "#242220" }}>Vissza</button>
        </div>
      </Layout>
    );
  }

  const lessons = course?.lessons || [];
  const currentIdx = lessons.findIndex((l) => l.id === lessonId);
  const prevLesson = currentIdx > 0 ? lessons[currentIdx - 1] : null;
  const nextLesson = currentIdx < lessons.length - 1 ? lessons[currentIdx + 1] : null;

  const handleQuizPass = () => setQuizDone(true);

  const goNext = () => {
    if (nextLesson) navigate(`/courses/${courseId}/lessons/${nextLesson.id}`);
    else navigate(`/courses/${courseId}`);
  };

  return (
    <Layout>
      <div data-testid="lesson-page">
        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="flex items-center gap-2 mb-6 font-bold text-sm"
          style={{ color: "#242220", background: "none", border: "none", cursor: "pointer" }}
          data-testid="back-to-course-btn"
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
          Vissza a kurzushoz
        </button>

        <div className="mb-6 flex items-center gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ background: "#FBD556", borderRadius: "14px", border: "1.5px solid #242220" }}
          >
            <BookOpen size={18} strokeWidth={2.5} color="#242220" />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.5 }}>
              {currentIdx + 1}. lecke / {lessons.length}
            </p>
            <h1 className="text-2xl font-bold" style={{ color: "#242220" }}>{lesson.title}</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 items-start">
          <div
            className="lg:col-span-2 p-6"
            style={{
              background: "#fff",
              borderRadius: "24px",
              border: "1.5px solid #242220",
              boxShadow: "4px 4px 0 #242220",
            }}
            data-testid="lesson-content"
          >
            <div
              className="prose prose-sm max-w-none"
              style={{ fontFamily: "Quicksand, sans-serif", color: "#242220", lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: lesson.content }}
            />
          </div>

          <div
            className="p-6 flex flex-col items-center justify-start"
            style={{
              background: "#FBD556",
              borderRadius: "24px",
              border: "1.5px solid #242220",
              boxShadow: "4px 4px 0 #242220",
            }}
          >
            <AppFeatureShowcase
              features={lesson.app_features || []}
              title="App Funkciók"
              accentColor="#FBD556"
            >
              <PhoneMockup image={lesson.phone_screenshot || course?.cover_image} />
            </AppFeatureShowcase>
            <p className="text-xs font-bold text-center mt-4" style={{ color: "#242220", opacity: 0.7 }}>
              Kattints a számokra a funkciók felfedezéséhez!
            </p>
          </div>
        </div>

        {lesson.quiz_questions && lesson.quiz_questions.length > 0 && (
          <QuizSection
            key={`${lessonId}-${quizKey}`}
            questions={lesson.quiz_questions}
            lessonId={lessonId}
            courseIdentifier={decodeURIComponent(courseId)}
            userId={user?.id}
            token={token}
            onPass={handleQuizPass}
            alreadyPassed={quizDone}
            onRetry={() => setQuizKey((k) => k + 1)}
          />
        )}

        <div className="flex justify-between items-center mt-8 gap-4">
          <button
            onClick={() => prevLesson && navigate(`/courses/${courseId}/lessons/${prevLesson.id}`)}
            disabled={!prevLesson}
            className="flex items-center gap-2 px-5 py-3 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
            style={{
              background: prevLesson ? "#FDF5EB" : "#e0e0e0",
              borderRadius: "30px",
              border: "1.5px solid #242220",
              boxShadow: prevLesson ? "3px 3px 0 #242220" : "none",
              color: "#242220",
              cursor: prevLesson ? "pointer" : "not-allowed",
              opacity: prevLesson ? 1 : 0.5,
            }}
            data-testid="prev-lesson-btn"
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
            Előző lecke
          </button>

          {(quizDone || !lesson.quiz_questions?.length) && (
            <button
              onClick={goNext}
              className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
              style={{
                background: "#A1E1B9",
                borderRadius: "30px",
                border: "1.5px solid #242220",
                boxShadow: "4px 4px 0 #242220",
                color: "#242220",
                cursor: "pointer",
              }}
              data-testid="next-lesson-btn"
            >
              {nextLesson ? "Következő lecke" : "Vissza a kurzushoz"}
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
